import React, { createContext, useEffect, useState } from 'react';
import { useAuth, useUser, useSignUp } from '@clerk/clerk-react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import {
  getFirestore,
  getDocs,
  doc,
  arrayUnion,
  updateDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
} from 'firebase/firestore';
import { sendEmail } from '../utils';

const firebaseConfig = {
  apiKey: process.env.GATSBY_FIREBASE_API_KEY,
  authDomain: process.env.GATSBY_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.GATSBY_FIREBASE_DATABASE_URL,
  projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
  storageBucket: process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: '764404880916',
  appId: '1:764404880916:web:f8fab479b687cdf3acf901',
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const defaultValues = {
  companiesData: [],

  isPopupOpen: false,
  togglePopupOpen: () => {},

  getData: () => {},
  getPendingCompanyData: () => {},
  saveQuestionnaireData: () => {},
  updateFirebase: () => {},
  deleteFirebase: () => {},

  runSignUp: () => {},
  runApproval: () => {},
};

export const SiteContext = createContext(defaultValues);

const SiteProvider = ({ children }) => {
  const [companiesData, setCompaniesData] = useState(defaultValues.company);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const { user, isSignedIn } = useUser();
  const { signUp } = useSignUp();
  const { getToken } = useAuth();
  const auth = getAuth(app);

  useEffect(() => {
    async function fetchData() {
      await getData();
    }
    isSignedIn && fetchData();
  }, [isSignedIn]);

  const togglePopupOpen = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  const generatePassword = () => {
    const length = 12;
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()-_=+';
    return Array.from({ length }, () =>
      charset.charAt(Math.floor(Math.random() * charset.length)),
    ).join('');
  };

  const getFirebaseData = async (collectionName, state) => {
    try {
      // user.organizationMemberships // To use later for choosing dashboards

      const firebaseToken = await getToken({
        template: 'integration_firebase',
      });

      const userCredentials = await signInWithCustomToken(auth, firebaseToken);

      let companyFirebaseData = [];

      const companyQuery = query(
        collection(db, collectionName),
        where('allowedUsers', 'array-contains', userCredentials.user.uid),
      );

      const querySnapshot = await getDocs(companyQuery);
      querySnapshot.forEach(doc => {
        companyFirebaseData.push(doc.data());
      });

      companyFirebaseData.length > 0 && state && state(companyFirebaseData);

      // Return the first company to redirect (if no organisation)
      return companyFirebaseData;
    } catch (e) {
      console.error(e);
    }
  };

  const getData = async () => {
    try {
      const data = await getFirebaseData('companies', setCompaniesData);
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  const getPendingCompanyData = async () => {
    try {
      const data = await getFirebaseData('pendingCompanies');
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  const updateFirebase = async (companyId, data) => {
    try {
      const companyRef = doc(db, 'companies', companyId.toString());

      await updateDoc(companyRef, data);

      return { successful: true };
    } catch (e) {
      console.error(e);
      return { successful: false };
    }
  };

  const deleteFirebase = async companyId => {
    try {
      deleteDoc(doc(db, 'pendingCompanies', companyId.toString()));

      return { successful: true };
    } catch (e) {
      console.error(e);
      return { successful: false };
    }
  };

  const saveQuestionnaireData = async (companyId, questionnaireData) => {
    try {
      let totalScore = 0;

      questionnaireData.responses.map(({ score }) => (totalScore += score));

      const companyRef = doc(db, 'companies', companyId.toString());

      await updateDoc(companyRef, {
        completedQuestionnaires: arrayUnion(questionnaireData),
      });

      await updateDoc(companyRef, {
        score: totalScore,
      });

      return { successful: true };
    } catch (e) {
      console.error(e);
      return { successful: false };
    }
  };

  const runSignUp = async formData => {
    try {
      window.grecaptcha.ready(function () {
        window.grecaptcha
          .execute(process.env.GATSBY_GOOGLE_RECAPTURE_SITE_KEY, {
            action: 'submit',
          })
          .then(async function (token) {
            // Add your logic to submit to your backend server here.
            // Check token
            const recaptureResponse = await fetch(
              '/.netlify/functions/recaptcha',
              {
                method: 'POST',
                body: JSON.stringify({
                  token: token,
                }),
              },
            ).then(response => response.json());

            if (recaptureResponse.score >= 0.7) {
              // Add Firebase data
              const firstName = formData.get('first-name');
              const lastName = formData.get('last-name');
              const organisationName = formData.get('organisation-name');
              const website = formData.get('website');
              const email = formData.get('email');
              const size = formData.get('number-of-staff');
              const turnover = formData.get('turnover');

              // Add Greencode members
              // Get from Clerk org
              const orgMembers = await fetch('/.netlify/functions/clerk', {
                method: 'POST',
                body: JSON.stringify({
                  type: 'org-users',
                }),
              })
                .then(response => response.json())
                .catch(e => {
                  console.log(e);
                });

              const allowedUsers = [];

              if (orgMembers && orgMembers.data && orgMembers.data.length > 0) {
                orgMembers.data.forEach(user => {
                  allowedUsers.push(user.public_user_data.user_id);
                });
              }

              await setDoc(doc(db, 'pendingCompanies', organisationName), {
                allowedUsers: allowedUsers,
                approved: false,
                paid: false,
                certified: 'No',
                contact: `${firstName} ${lastName}`,
                website: website,
                email: email,
                size: size,
                turnover: turnover,
                name: organisationName,
              });

              sendEmail(
                'Welcome',
                'welcome',
                {
                  firstName: firstName,
                },
                email,
              );

              return { successful: true };
            } else {
              return { successful: false };
            }
          });
      });
    } catch (e) {
      console.error(e);
      return { successful: false };
    }
  };

  const runApproval = async (data, isAdditional) => {
    try {
      // Add Clerk user
      const password = generatePassword();

      const newUser = await fetch('/.netlify/functions/clerk', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          password: password,
          type: 'approval',
        }),
      })
        .then(response => response.json())
        .catch(e => {
          console.log(e);
        });

      if (isAdditional) {
        await updateDoc(doc(db, 'companies', data.id.toString()), {
          allowedUsers: arrayUnion(newUser.id),
        });
      } else {
        const allowedUsers = [...data.allowedUsers, newUser.id];

        await setDoc(doc(db, 'companies', data.id.toString()), {
          ...data,
          allowedUsers: allowedUsers,
        });

        // delete from pendingCompanies

        data.name &&
          deleteDoc(doc(db, 'pendingCompanies', data.name.toString()));
      }

      return { successful: true };
    } catch (e) {
      console.error(e);
      return { successful: false };
    }
  };

  return (
    <SiteContext.Provider
      value={{
        companiesData,

        isPopupOpen,
        togglePopupOpen,

        getData,
        getPendingCompanyData,
        saveQuestionnaireData,
        updateFirebase,
        deleteFirebase,

        runSignUp,
        runApproval,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export default SiteProvider;
